import { Text } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { HardwareRequestStatus } from "src/api/types.generated";
import {
  errorToast,
  successToast,
  Header,
  Icon,
  PageWrapper,
  Spinner,
} from "src/shared/components";
import HardwareRequestsHistoryList from "src/shared/components/Hardware/RequestsList";
import { buildRequestDataById } from "src/shared/utils/hardware/buildRequestDataById";
import { formatHardwareRequestStatus } from "src/shared/utils/hardware/formatHardwareRequestStatus";
import { processRequests } from "src/shared/utils/hardware/processRequests";
import globalConstants from "src/theme/globalConstants";

import { useGetAllHardwareSkusQuery } from "../graphql/getAllHardwareSkus.generated";
import { useGetUserHardwareRequestsQuery } from "../graphql/getUserHardwareRequests.generated";
import { useUpdateHardwareRequestStatusMutation } from "../graphql/updateHardwareRequestStatus.generated";
import { getRandomHardwareLoadingMessage } from "../inventory/shared/loadingTextUtil";
import { printHardwarePackage } from "../shared/printHardwareLabel";

import RequestInfoModal from "./requestInfoModal";

interface RequestsTabProps {
  userId: number;
  onBack: () => void;
  name: string;
}

const RequestsTab: React.FC<RequestsTabProps> = ({ userId, onBack, name }) => {
  const { data: userHardwareRequestsData, loading } =
    useGetUserHardwareRequestsQuery({
      variables: {
        where: {
          user_id: { equals: userId },
        },
      },
    });
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{
    id: number;
    image: string;
    name: string;
    notes: string;
    quantity: number;
    sku_code: string;
    category_id: number;
    type: string;
  } | null>(null);
  const userHardwareRequests = userHardwareRequestsData?.hardwareRequests;

  const { data: hardwareItemsData } = useGetAllHardwareSkusQuery({
    fetchPolicy: "cache-first",
  });

  const hardwareItems = hardwareItemsData?.getHardwareSkus;

  // Get printer endpoint from localStorage
  const printerEndpoint =
    localStorage.getItem("hardware-printer-endpoint") || "";

  // Mutation for updating hardware request status
  const [updateHardwareRequestStatus] =
    useUpdateHardwareRequestStatusMutation();

  // Loading message for spinner
  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  // Calculate status counts for this user
  const userStatusCounts = useMemo(() => {
    if (!userHardwareRequests) return {};

    const counts: Record<HardwareRequestStatus, number> = {
      [HardwareRequestStatus.Waiting]: 0,
      [HardwareRequestStatus.Requested]: 0,
      [HardwareRequestStatus.Accepted]: 0,
      [HardwareRequestStatus.Ready]: 0,
      [HardwareRequestStatus.PickedUp]: 0,
      [HardwareRequestStatus.Returned]: 0,
      [HardwareRequestStatus.Denied]: 0,
      [HardwareRequestStatus.Cancelled]: 0,
    };

    userHardwareRequests.forEach((request) => {
      counts[request.status]++;
    });

    return counts;
  }, [userHardwareRequests]);

  // Process requests using the same logic as the organizer view
  const hardwareRequests = useMemo(() => {
    if (!userHardwareRequests || !hardwareItems) return [];

    // Sort requests by created_at in descending order (newest first)
    const sortedRequests = userHardwareRequests
      .slice()
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    return processRequests({
      data: sortedRequests,
      items: hardwareItems,
      userType: "organizer", // Use organizer view for better functionality
      getButtons: (requestStatus) => {
        // Remove all buttons from individual items - they should be at request level
        return [];
      },
      onClick: (requestId, skuCode) => {
        // Find the item and open the modal
        const request = userHardwareRequests.find(
          (req) => req.id === requestId
        );
        if (request?.hardware_item_requests) {
          const itemRequest = request.hardware_item_requests.find(
            (item) => item.HardwareItem.sku_code === skuCode
          );
          if (itemRequest) {
            const skuInfo = hardwareItems.find((sku) => sku.code === skuCode);
            if (skuInfo) {
              setSelectedRequest({
                id: itemRequest.id,
                image: skuInfo.image,
                name: skuInfo.name,
                notes: `Request #${request.id} - Status: ${request.status}`,
                quantity: itemRequest.quantity,
                sku_code: skuCode,
                category_id: skuInfo.category_id,
                type: skuInfo.type,
              });
              setRequestInfoModalOpen(true);
            }
          }
        }
      },
      hideLottery: false,
    });
  }, [userHardwareRequests, hardwareItems]);

  // Print label handler
  const handlePrintLabel = async (requestId: number, requestData: any) => {
    if (!printerEndpoint) {
      errorToast("Please set the printer endpoint URL in settings");
      return;
    }

    // Find the processed request to get the items
    const processedRequest = hardwareRequests.find((req) =>
      req.items.some((item) => item.requestId === requestId)
    );

    if (!processedRequest) {
      errorToast("Request not found");
      return;
    }

    // Filter items to only include those belonging to this specific request
    const requestItems = processedRequest.items.filter(
      (item) => item.requestId === requestId
    );

    const printData = {
      request_id: requestId,
      user_name: requestData?.user?.name || "Unknown",
      user_email: requestData?.user?.email || "No email",
      items: requestItems.map((item) => ({
        title: item.title,
        description: item.description,
        label: item.label,
        quantity: item.quantity,
      })),
    };

    try {
      await printHardwarePackage(printData);
      successToast("Label sent to printer successfully");
    } catch (e) {
      errorToast((e as Error)?.message || "Failed to print label");
    }
  };

  // Status update handler
  const handleUpdateRequestStatus = async (
    requestId: number,
    status: HardwareRequestStatus
  ) => {
    try {
      await updateHardwareRequestStatus({
        variables: {
          id: requestId,
          status,
        },
      });
      console.log(
        `Successfully updated request ${requestId} to status ${status}`
      );
    } catch (error) {
      console.error("Error updating request status:", error);
    }
  };

  if (loading) {
    return <Spinner text={loadingMessage} />;
  }

  return (
    <>
      <Header
        title="Hardware"
        subtitle="Browse and request a variety of hardware to bring your project to life!"
      />
      <PageWrapper>
        <BackButton onClick={onBack}>
          <Icon
            name="chevron-left"
            color={globalConstants?.color?.textSecondary as string}
            style={{ width: "24px", height: "24px" }}
          />
          <BackText>Back</BackText>
        </BackButton>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "16px",
            marginBottom: "24px",
          }}
        >
          <Text type="h2">{`${name}'s requests`}</Text>
        </div>

        {/* User Status Counts */}
        <UserStatusCountsSection>
          <Text type="h3" style={{ marginBottom: "16px" }}>
            {name}&apos;s Request Status Overview
          </Text>
          <UserStatusCountsGrid>
            {Object.entries(userStatusCounts).map(([status, count]) => (
              <UserStatusCountCard key={status}>
                <UserStatusCountNumber>{count as number}</UserStatusCountNumber>
                <UserStatusCountLabel>
                  {formatHardwareRequestStatus(status)}
                </UserStatusCountLabel>
              </UserStatusCountCard>
            ))}
          </UserStatusCountsGrid>
        </UserStatusCountsSection>
        {selectedRequest && (
          <RequestInfoModal
            isOpen={requestInfoModalOpen}
            onClose={() => setRequestInfoModalOpen(false)}
            requestId={selectedRequest.id}
            itemName={selectedRequest.name}
            skuCode={selectedRequest.sku_code}
            categoryId={selectedRequest.category_id}
            type={selectedRequest.type}
          />
        )}
        <HardwareRequestsHistoryList
          hardwareRequests={hardwareRequests.map((request) => ({
            ...request,
            requestDataById: buildRequestDataById(
              request.items.map((item) => item.requestId),
              userHardwareRequests
            ),
          }))}
          userType="organizer"
          onPrintLabel={handlePrintLabel}
          onUpdateStatus={handleUpdateRequestStatus}
          printerEndpoint={printerEndpoint}
        />
      </PageWrapper>
    </>
  );
};

const BackButton = styled.button`
  display: flex;
  padding: 6px 0px;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const BackText = styled.button`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  color: ${globalConstants?.color?.textSecondary as string};
`;

const UserStatusCountsSection = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
`;

const UserStatusCountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
`;

const UserStatusCountCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const UserStatusCountNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  margin-bottom: 4px;
`;

const UserStatusCountLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-align: center;
  line-height: 1.2;
`;

export default RequestsTab;
